/**
 * The second-factor screens a sign-in swaps to (module-private to `<LoginForm>`): the N152 enrolment
 * hand-off, the first-factor enrolment (Q-L16-98 · F-98-16 / Q-ARG-1), and the MFA verification —
 * whose refused code is shown from the held refusal (W16), translated and carrying its terminal tone.
 *
 * A function, not a component: it answers the screen to render, or `null` when the sign-in is still on
 * its credential step.
 *
 * @module @puffin-core/login-shell/components/loginForm/secondFactorStep
 */

import { MfaVerification } from '@puffin-core/mfa-ui/verification';
import { MfaEnrollment } from '@puffin-core/mfa-ui/enrollment';
import { MfaEnrollmentHandoff } from '../MfaEnrollmentHandoff.jsx';

/**
 * @param {Object} options
 * @param {Object} options.form - `useLoginForm`'s state.
 * @param {Object} options.auth - The surface's auth contract.
 * @param {string} [options.appName] - The brand lockup the enrolment screen names.
 * @param {Object} [options.redirectConfig]
 * @param {(key: string) => string} options.translate - The `login`-namespace translator.
 * @param {Function} [options.t] - The consumer translator.
 * @returns {?React.JSX.Element}
 */
export function renderSecondFactorStep({ form, auth, appName, redirectConfig, translate, t }) {
  // Q-L16-98 · F-98-16 / Q-ARG-1 — a platform staff member with no second factor, or a tenant person whose MFA an
  // administrator's reset cleared, enrols here through the sign-in's enrolment token, and is signed in when the backup
  // codes are acknowledged.
  // N152 — the marketing apex holds the same person, and hands them to their company's sign-in page to enrol there.
  if (form.mfaEnrollmentHandoff && redirectConfig?.buildTenantLoginUrl) {
    return (
      <MfaEnrollmentHandoff
        tenantLoginUrl={redirectConfig.buildTenantLoginUrl(form.mfaEnrollmentHandoff.subdomain)}
        onBack={form.handleMfaCancel}
        t={t}
      />
    );
  }

  if (form.mfaEnrollmentRequired) {
    return (
      <MfaEnrollment
        appName={appName}
        enrollment={auth.mfaEnrollment}
        loading={form.isLoading}
        error={form.error}
        onBegin={auth.beginMfaEnrollment}
        onComplete={auth.completeMfaEnrollment}
        onFinish={form.handleMfaEnrollmentFinish}
        onCancel={auth.cancelMfaEnrollment}
        t={t}
      />
    );
  }

  if (form.mfaRequired) {
    // W16: the held refusal, in the reader's language, carrying its terminal tone; else the auth
    // context's own error.
    const mfaError = form.mfaRefusal
      ? Object.assign(new Error(translate(form.mfaRefusal.messageKey)), {
          terminal: form.mfaRefusal.terminal,
        })
      : form.error;
    return (
      <MfaVerification
        methods={form.mfaMethods}
        preferredMethod={form.preferredMethod}
        maskedPhone={form.maskedPhone}
        maskedEmail={form.maskedEmail}
        onVerify={form.handleMfaVerify}
        onVerifyPasskey={form.handleMfaPasskeyVerify}
        onCancel={form.handleMfaCancel}
        isLoading={form.isLoading}
        error={mfaError}
        t={t}
      />
    );
  }

  return null;
}
